import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAyadi } from "../api/vastuTools.api";
import normalizeApiError from "../utils/normalizeApiError";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import KeyValueTable from "../components/ui/KeyValueTable";
import LoadingState from "../components/ui/LoadingState";

export default function DashboardAyadi() {
  const [toolData, setToolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    const fetchTool = async () => {
      try {
        const data = await getAyadi();
        setToolData(data || null);
        setLoadError(null);
      } catch (error) {
        setToolData(null);
        setLoadError(normalizeApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchTool();
  }, []);

  const inputs = useMemo(() => {
    const raw = toolData?.inputs;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([key, value]) => ({
        key,
        value,
      }));
    }
    return [];
  }, [toolData]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {toolData?.title || "Ayadi"}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {toolData?.value ? "Calculated output from Ayadi tool." : "Ayadi tool"}
        </p>
      </div>
      {loading ? (
        <LoadingState />
      ) : loadError ? (
        <ErrorState error={loadError} />
      ) : !toolData ? (
        <EmptyState
          title="No Ayadi data"
          message="Enter inputs to view Ayadi details."
          testId="dashboard-empty-ayadi"
        />
      ) : (
        <>
          {inputs.length > 0 && (
            <div className="card card-hover p-6">
              <p className="text-sm font-semibold text-slate-900">Inputs</p>
              <div className="mt-3 grid gap-3">
                {inputs.map((input, index) => {
                  const isKeyValue =
                    typeof input === "object" && input !== null && "key" in input;
                  const label = isKeyValue ? String(input.key) : `Input ${index + 1}`;
                  const value = isKeyValue ? input.value : input;
                  const isObjectValue = value && typeof value === "object";
                  return (
                    <div
                      key={`ayadi-input-${index}`}
                      className="rounded-md border border-slate-100 p-3 text-sm text-slate-600"
                    >
                      <p className="text-xs font-semibold text-slate-500">{label}</p>
                      {isObjectValue ? (
                        <div className="mt-2">
                          <KeyValueTable data={value} />
                        </div>
                      ) : (
                        <p className="mt-1 text-sm text-slate-600">
                          {value ?? "—"}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="card card-hover p-6">
            <p className="text-sm font-semibold text-slate-900">
              {toolData.title || "Output"}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              {toolData.value ?? "—"}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
